import {
  accountStatusUpdated,
  appOpened,
  appSync,
  setFirstName,
  socketConnected,
  startListening,
} from '@bonanza-social/toolkit/state';
import { isAnyOf } from '@reduxjs/toolkit';
import {
  getAccount,
  selectAccountStatus,
  setAccount,
  switchLanguage,
} from './account.slice';
import { AccountStatusChangedEventData } from './account.types';
import { logout } from '../../../../auth/lib/state/auth/auth.slice';
import { BottomSheet } from '@bonanza-social/navigation';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import {
  SOF_BOTTOM_SHEET_LAST_SHOW_TIME_STORAGE_KEY,
  SOF_BOTTOM_SHEET_SHOW_INTERVAL,
} from '../../bottomSheets';
import { IS_WEB } from '@bonanza-social/moonlight/lib/common/KeyboardShift/KeyboardShift.content';
import {
  PREFERRED_LANGUAGE_STORAGE_KEY,
  SUGGESTED_LANGUAGE_COOKIE_NAME,
} from '../../constants';
import { getCookie } from '@bonanza-social/toolkit/utils';
import i18next from '@bonanza-social/i18n';
startListening({
  actionCreator: appSync,
  effect: (_, { dispatch }) => {
    dispatch(getAccount());
  },
});

startListening({
  actionCreator: socketConnected,
  effect: (action, { dispatch }) => {
    const connection = action.payload;

    connection.on(
      'AccountStatusChanged',
      (data: AccountStatusChangedEventData) => {
        dispatch(
          setAccount({
            status: data.accountStatus,
            statusReason: data.accountStatusReason,
            verificationMeta: data.verificationMeta,
          }),
        );
        dispatch(accountStatusUpdated());
      },
    );
  },
});

startListening({
  matcher: isAnyOf(getAccount.fulfilled, setAccount),
  effect: (_, { getState, dispatch, cancelActiveListeners }) => {
    cancelActiveListeners();

    const state = getState();
    const accountStatus = selectAccountStatus(state);

    if (
      accountStatus === 'Suspended' ||
      accountStatus === 'Closed' ||
      accountStatus === 'SelfExclusion'
    ) {
      dispatch(logout());
    }
  },
});

startListening({
  actionCreator: getAccount.fulfilled,
  effect: async (action, { dispatch }) => {
    const { sourceOfFundsForm, personalDetails } = action.payload;
    const { firstName = '' } = personalDetails ?? {};

    dispatch(setFirstName({ firstName }));

    if (!sourceOfFundsForm || sourceOfFundsForm.isCompleted) {
      return;
    }

    const { id } = sourceOfFundsForm;

    const SOFBottomSheetAsyncStorageData =
      (await AsyncStorage.getItem(
        SOF_BOTTOM_SHEET_LAST_SHOW_TIME_STORAGE_KEY,
      )) ?? '{}';

    const { formId, time: lastShowTime } = await JSON.parse(
      SOFBottomSheetAsyncStorageData,
    );

    if (
      Date.now() - lastShowTime >= SOF_BOTTOM_SHEET_SHOW_INTERVAL ||
      id !== formId
    ) {
      BottomSheet.open({
        namespace: 'account',
        type: 'SOF',
      });
    }
  },
});

startListening({
  actionCreator: getAccount.fulfilled,
  effect: (action) => {
    const { rgQuestionnaireForm } = action.payload;

    if (rgQuestionnaireForm) {
      BottomSheet.open({
        namespace: 'account',
        type: 'RGQuestionnaire',
        closeOnBlur: !rgQuestionnaireForm.isMandatory,
        showCloseIcon: !rgQuestionnaireForm.isMandatory,
        enablePanDownToClose: !rgQuestionnaireForm.isMandatory,
      });
    }
  },
});

startListening({
  actionCreator: appOpened,
  effect: async () => {
    const langParameter = IS_WEB
      ? new URLSearchParams(window.location.search).get('lang')
      : null;
    const storedLanguage = await AsyncStorage.getItem(
      PREFERRED_LANGUAGE_STORAGE_KEY,
    );

    const cookieLang = await getCookie(SUGGESTED_LANGUAGE_COOKIE_NAME);

    if (storedLanguage) {
      void i18next.changeLanguage(storedLanguage as unknown as string);
      return;
    }

    if (langParameter === 'en' || langParameter === 'fr') {
      void i18next.changeLanguage(langParameter);
      void AsyncStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, langParameter);
      return;
    }

    if (cookieLang) {
      void i18next.changeLanguage(cookieLang as unknown as string);
    }
  },
});

startListening({
  actionCreator: switchLanguage,
  effect: async ({ payload }) => {
    await AsyncStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, payload);
    await i18next.changeLanguage(payload);
  },
});
